import { useEffect, useState, useCallback, Fragment } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import client from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, RefreshCw, TriangleAlert, CheckCircle2, Send, FileText, BarChart3, Phone, Upload } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { IDENTIFICATION_QUESTIONS, UNIVERSAL_QUESTIONS, CATEGORY_QUESTIONS, CATEGORY_NOTES, CLOSING_QUESTIONS } from "@/lib/intakeQuestions";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

const box = "border border-[#2C2D35] bg-[#16171B]";
const inp = "w-full bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-xs outline-none focus:border-[#3B82F6] font-mono text-[#F3F4F6]";
const btn = "px-3 py-1.5 text-[11px] font-head font-bold rounded-sm text-white disabled:opacity-40";

const TILES = [
  ["new_complaints", "NEW COMPLAINTS", "#3B82F6"], ["unassigned", "UNASSIGNED", "#F59E0B"],
  ["active_investigations", "ACTIVE INVESTIGATIONS", "#8B5CF6"], ["pending_supervisor_review", "SUPERVISOR REVIEW", "#EAB308"],
  ["command_review", "COMMAND REVIEW", "#F97316"], ["criminal_referrals", "CRIMINAL REFERRALS", "#DC2626"],
  ["closed_this_month", "CLOSED THIS MONTH", "#10B981"], ["overdue", "OVERDUE", "#EF4444"],
];

export default function IA() {
  const [dash, setDash] = useState(null);
  const [cases, setCases] = useState([]);
  const [sel, setSel] = useState(null);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("cases");
  const [analytics, setAnalytics] = useState(null);
  useEffect(() => {
    if (tab === "analytics" && !analytics) client.get("/ia/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  }, [tab, analytics]);
  const load = useCallback(async () => {
    const [d, c] = await Promise.all([client.get("/ia/dashboard"), client.get("/ia/cases")]);
    setDash(d.data); setCases(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const openCase = async (no) => {
    const r = await client.get(`/ia/cases/${no}`);
    setSel(r.data);
  };
  const act = async (path, payload, msg) => {
    try {
      await client.post(`/ia/cases/${sel.public_no}/${path}`, payload);
      toast.success(msg);
      await load();
      await openCase(sel.public_no);
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };
  const shown = cases.filter((c) => filter === "all" ? true : filter === "complaint" ? c.kind === "complaint" : filter === "commendation" ? c.kind === "commendation" : filter === "open" ? c.status !== "closed" : c.status === "closed");
  return (
    <Layout>
      <PageHeader icon={ShieldAlert} seal="/brand/ia-seal.png" title="INTERNAL AFFAIRS CONSOLE" subtitle="Matcom Internal Affairs · Professional standards · complaint intake · investigations · approval chain"
        right={
          <div className="flex items-center gap-1">
            <button data-testid="ia-tab-cases" onClick={() => setTab("cases")} className="px-3 py-1.5 text-[11px] font-head font-bold rounded-sm" style={tab === "cases" ? { background: "#3B82F6", color: "#fff" } : { color: "#9CA3AF" }}>CASES</button>
            <button data-testid="ia-tab-analytics" onClick={() => setTab("analytics")} className="px-3 py-1.5 text-[11px] font-head font-bold rounded-sm flex items-center gap-1" style={tab === "analytics" ? { background: "#3B82F6", color: "#fff" } : { color: "#9CA3AF" }}><BarChart3 size={13} /> ANALYTICS</button>
            <button data-testid="ia-tab-records" onClick={() => setTab("records")} className="px-3 py-1.5 text-[11px] font-head font-bold rounded-sm" style={tab === "records" ? { background: "#3B82F6", color: "#fff" } : { color: "#9CA3AF" }}>RECORDS REQUESTS</button>
            <button data-testid="ia-tab-phone" onClick={() => setTab("phone")} className="px-3 py-1.5 text-[11px] font-head font-bold rounded-sm flex items-center gap-1" style={tab === "phone" ? { background: "#10B981", color: "#fff" } : { color: "#9CA3AF" }}><Phone size={13} /> PHONE INTAKE</button>
            <button data-testid="ia-refresh" onClick={load} className="p-2 text-[#9CA3AF] hover:text-white"><RefreshCw size={15} /></button>
          </div>
        } />
      {tab === "analytics" ? <AnalyticsView a={analytics} /> : tab === "records" ? <RecordsView /> : tab === "phone" ? <PhoneIntakeView onDone={load} /> : (<>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        {dash && TILES.map(([k, label, color]) => (
          <div key={k} data-testid={`ia-tile-${k}`} className={`${box} p-3`}>
            <div className="font-mono text-2xl font-bold" style={{ color }}>{dash[k]}</div>
            <div className="font-mono text-[9px] tracking-wider text-[#9CA3AF]">{label}</div>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-5 gap-3">
        <div className={`lg:col-span-2 ${box}`}>
          <div className="px-3 py-2 border-b border-[#2C2D35] flex items-center gap-1 flex-wrap">
            {["all", "open", "closed", "complaint", "commendation"].map((f) => (
              <button key={f} data-testid={`ia-filter-${f}`} onClick={() => setFilter(f)} className="px-2 py-1 text-[10px] font-head rounded-sm uppercase"
                style={filter === f ? { background: "#3B82F6", color: "#fff" } : { color: "#9CA3AF" }}>{f}</button>))}
          </div>
          <div className="max-h-[62vh] overflow-y-auto divide-y divide-[#2C2D35]">
            {shown.map((c) => (
              <button key={c.id} data-testid={`ia-case-${c.public_no}`} onClick={() => openCase(c.public_no)}
                className="w-full text-left px-3 py-2.5 hover:bg-[#1E1F24]" style={sel?.public_no === c.public_no ? { background: "#1E2634" } : {}}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs font-bold text-white">{c.internal_no}</span>
                  <span className="px-1.5 py-0.5 text-[8px] font-bold rounded-sm" style={{ background: c.kind === "complaint" ? "#DC262622" : "#10B98122", color: c.kind === "complaint" ? "#F87171" : "#34D399" }}>{c.kind.toUpperCase()}</span>
                  {c.possible_criminal && <span className="px-1.5 py-0.5 text-[8px] font-bold rounded-sm bg-[#DC2626] text-white">⚠ CRIMINAL</span>}
                  <span className="ml-auto font-mono text-[9px] text-[#9CA3AF]">{c.current_stage.replace(/_/g, " ").toUpperCase()}</span>
                </div>
                <div className="text-[11px] text-[#C9CDD4] mt-0.5 truncate">{c.categories.join(", ")}{c.officer_name ? ` · ${c.officer_name}` : ""}</div>
                <div className="font-mono text-[9px] text-[#6B7280]">{new Date(c.created_at).toLocaleDateString()} · {c.evidence_count} evidence · {c.investigator || "unassigned"}{c.status === "closed" ? " · CLOSED" : ""}</div>
              </button>
            ))}
            {!shown.length && <div className="p-6 text-center font-mono text-xs text-[#6B7280]">No cases</div>}
          </div>
        </div>
        <div className={`lg:col-span-3 ${box} max-h-[70vh] overflow-y-auto`}>
          {!sel ? <div className="p-10 text-center font-mono text-xs text-[#6B7280]">Select a case to open the investigator workspace</div>
            : <CaseDetail sel={sel} dash={dash} act={act} />}
        </div>
      </div>
      </>)}
    </Layout>
  );
}

function AnalyticsView({ a }) {
  if (!a) return <div className="p-10 text-center font-mono text-xs text-[#6B7280]">Loading analytics…</div>;
  const box = "border border-[#2C2D35] bg-[#16171B]";
  const chartData = a.months.map((m) => ({ ...m, label: m.month.slice(2) }));
  return (
    <div data-testid="ia-analytics" className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div data-testid="ia-stat-sustained-rate" className={`${box} p-3`}>
          <div className="font-mono text-2xl font-bold" style={{ color: "#F59E0B" }}>{a.sustained_rate_pct != null ? `${a.sustained_rate_pct}%` : "—"}</div>
          <div className="font-mono text-[9px] tracking-wider text-[#9CA3AF]">SUSTAINED RATE ({a.sustained}/{a.total_findings} ALLEGATIONS)</div>
        </div>
        <div data-testid="ia-stat-ratio" className={`${box} p-3`}>
          <div className="font-mono text-2xl font-bold" style={{ color: "#3B82F6" }}>{a.complaint_to_commendation_ratio ?? "—"}</div>
          <div className="font-mono text-[9px] tracking-wider text-[#9CA3AF]">COMPLAINT : COMMENDATION RATIO</div>
        </div>
        <div className={`${box} p-3`}>
          <div className="font-mono text-2xl font-bold text-white">{a.totals_12mo.complaints} / {a.totals_12mo.commendations}</div>
          <div className="font-mono text-[9px] tracking-wider text-[#9CA3AF]">COMPLAINTS / COMMENDATIONS (12 MO)</div>
        </div>
        <div className={`${box} p-3`}>
          <div className="font-mono text-2xl font-bold" style={{ color: "#10B981" }}>{a.average_investigation_days ?? "—"}</div>
          <div className="font-mono text-[9px] tracking-wider text-[#9CA3AF]">AVG INVESTIGATION DAYS</div>
        </div>
      </div>
      <div className={`${box} p-4`} data-testid="ia-trend-chart">
        <div className="font-head text-[10px] font-bold tracking-wider text-[#9CA3AF] mb-3">COMPLAINTS BY MONTH · COMMENDATION TREND (12 MONTHS)</div>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={chartData}>
            <CartesianGrid stroke="#2C2D35" strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }} />
            <YAxis allowDecimals={false} tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }} />
            <Tooltip contentStyle={{ background: "#16171B", border: "1px solid #2C2D35", fontSize: 11, fontFamily: "monospace" }} labelStyle={{ color: "#fff" }} />
            <Legend wrapperStyle={{ fontSize: 10, fontFamily: "monospace" }} />
            <Bar dataKey="complaints" name="Complaints" fill="#DC2626" radius={[2, 2, 0, 0]} />
            <Line type="monotone" dataKey="commendations" name="Commendations" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className={`${box} p-4`} data-testid="ia-top-allegations">
        <div className="font-head text-[10px] font-bold tracking-wider text-[#9CA3AF] mb-3">TOP ALLEGATIONS</div>
        {a.top_allegations.length === 0 && <div className="font-mono text-xs text-[#6B7280]">No complaint data yet.</div>}
        {a.top_allegations.map(([cat, n]) => {
          const max = a.top_allegations[0][1];
          return (
            <div key={cat} className="flex items-center gap-2 mb-1.5">
              <div className="w-64 text-xs text-[#C9CDD4] truncate">{cat}</div>
              <div className="flex-1 h-4 bg-[#141518] rounded-sm overflow-hidden">
                <div className="h-full rounded-sm" style={{ width: `${(n / max) * 100}%`, background: "#3B82F6" }} />
              </div>
              <div className="font-mono text-xs text-white w-8 text-right">{n}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CaseDetail({ sel, dash, act }) {
  const [referral, setReferral] = useState("Internal Affairs");
  const [note, setNote] = useState("");
  const [investigator, setInvestigator] = useState("");
  const [findings, setFindings] = useState({});
  const [crim, setCrim] = useState({ referral_decision: "referred", agency_referred_to: "", referral_number: "", reason: "" });
  const [draft, setDraft] = useState("");
  const stage = sel.current_stage;
  const closed = sel.status === "closed";
  const sec = "px-4 py-3 border-b border-[#2C2D35]";
  const h = "font-head text-[10px] font-bold tracking-wider text-[#9CA3AF] mb-2";
  return (
    <div data-testid="ia-case-detail">
      <div className={sec} style={{ background: "#1A2230" }}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono font-bold text-white">{sel.internal_no}</span>
          {sel.criminal_case_no && <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-[#7F1D1D] text-white">CRIMINAL: {sel.criminal_case_no}</span>}
          <span className="ml-auto font-mono text-[10px] text-[#9CA3AF]">STAGE: {stage.replace(/_/g, " ").toUpperCase()}{closed ? " · CLOSED" : ""}</span>
          <button data-testid="ia-case-file-pdf" onClick={async () => {
            try {
              const r = await client.get(`/ia/cases/${sel.internal_no}/case-file.pdf`, { responseType: "blob" });
              const url = URL.createObjectURL(r.data);
              const a = document.createElement("a");
              a.href = url; a.download = `IA_CaseFile_${sel.internal_no}.pdf`;
              document.body.appendChild(a); a.click(); a.remove();
              URL.revokeObjectURL(url);
              toast.success("Sealed case file exported");
            } catch { toast.error("Case file export failed"); }
          }} title="Export complete sealed IA case packet (complaint, evidence log, findings, approvals, audit) for command review"
            className="px-2 py-1 text-[10px] font-head font-bold rounded-sm text-black flex items-center gap-1" style={{ background: "#F59E0B" }}>
            <FileText size={11} /> CASE FILE PDF
          </button>
        </div>
        {sel.possible_criminal && !sel.criminal_review && (
          <div data-testid="ia-criminal-banner" className="mt-2 px-3 py-2 flex items-center gap-2 text-xs font-bold text-white rounded-sm" style={{ background: "#DC2626" }}>
            <TriangleAlert size={14} /> POSSIBLE CRIMINAL ALLEGATION — Criminal Referral Review required before command approval
          </div>
        )}
        {sel.possible_criminal && sel.criminal_review && (
          <div data-testid="ia-criminal-documented" className="mt-2 px-3 py-2 flex items-center gap-2 text-xs font-bold rounded-sm" style={{ background: "#14432A", color: "#4ade80" }}>
            <CheckCircle2 size={14} /> CRIMINAL REFERRAL REVIEW DOCUMENTED — {sel.criminal_review.referral_decision === "referred" ? `Referred to ${sel.criminal_review.agency_referred_to}` : "Referral declined with documented reason"} by {sel.criminal_review.reviewed_by}
          </div>
        )}
      </div>
      <div className={sec}>
        <div className={h}>CASE SUMMARY</div>
        <div className="text-xs text-[#C9CDD4] space-y-1">
          <div><b className="text-white">Allegations:</b> {sel.categories.join(", ")}</div>
          <div><b className="text-white">Complainant:</b> {sel.anonymous ? "Anonymous" : `${sel.complainant?.name || "—"} (${sel.complainant?.email || sel.complainant?.phone || "—"})`}</div>
          <div><b className="text-white">Subject:</b> {sel.incident?.officer_name || "—"} {sel.incident?.badge_number ? `#${sel.incident.badge_number}` : ""} {sel.incident?.unit_number || ""}</div>
          <div><b className="text-white">Incident:</b> {sel.incident?.date || "—"} {sel.incident?.time || ""} · {sel.incident?.location || "—"} · CAD {sel.incident?.cad_number || "—"}</div>
          <div className="p-2 bg-[#141518] border border-[#2C2D35] rounded-sm mt-1 whitespace-pre-wrap">{sel.description}</div>
          {sel.witnesses && <div><b className="text-white">Witnesses:</b> {sel.witnesses}</div>}
          {sel.requested_outcome && <div><b className="text-white">Requested outcome:</b> {sel.requested_outcome}</div>}
          {(sel.evidence || []).length > 0 && <div><b className="text-white">Evidence:</b> {sel.evidence.map((e) => `${e.eid} (${e.original_filename})`).join(", ")}</div>}
        </div>
      </div>
      {(sel.interview_transcript || []).length > 0 && (
        <div className={sec} data-testid="ia-transcript">
          <div className="flex items-center justify-between mb-2">
            <div className="font-head text-[10px] font-bold tracking-wider text-[#9CA3AF]">PHONE INTERVIEW TRANSCRIPT — recorded by {sel.taken_by || "staff"} at intake</div>
            <button data-testid="ia-transcript-pdf" onClick={async () => {
              try {
                const r = await client.get(`/ia/cases/${sel.internal_no}/transcript.pdf`, { responseType: "blob" });
                const url = URL.createObjectURL(r.data);
                const a = document.createElement("a");
                a.href = url; a.download = `transcript_${sel.internal_no}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              } catch { toast.error("Transcript export failed"); }
            }} className="px-2 py-1 text-[10px] font-head font-bold rounded-sm text-white flex items-center gap-1" style={{ background: "#3B82F6" }}>
              <FileText size={11} /> EXPORT PDF
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {sel.interview_transcript.map((s, si) => {
              const askedN = s.questions.filter((q) => q.asked).length;
              return (
                <div key={si} data-testid={`ia-transcript-section-${si}`}>
                  <div className="font-head text-[9px] font-bold tracking-wider mb-0.5" style={{ color: "#93C5FD" }}>
                    {s.section.toUpperCase()} <span className="text-[#6B7280]">{askedN}/{s.questions.length} asked</span>
                  </div>
                  {s.questions.map((q, qi) => (
                    <div key={qi} className="flex items-start gap-1.5 text-[11px] py-px" style={{ color: q.asked ? "#C9CDD4" : "#4B5563" }}>
                      <span className="font-mono w-3 shrink-0" style={{ color: q.asked ? "#10B981" : "#6B7280" }}>{q.asked ? "✓" : "—"}</span>
                      {q.q}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {sel.kind === "commendation" && !closed && (
        <div className={sec}>
          <div className={h}>COMMENDATION ROUTING (Community Relations — not IA)</div>
          <button data-testid="ia-commend-ack" onClick={() => act("commendation-ack", { recognition: "Letter of Commendation", note }, "Commendation acknowledged and entered in personnel record")}
            className={btn} style={{ background: "#10B981" }}><CheckCircle2 size={12} className="inline mr-1" />VERIFY + ACKNOWLEDGE + PERSONNEL RECORD</button>
        </div>
      )}
      {sel.kind === "complaint" && !closed && stage === "received" && (
        <div className={sec}>
          <div className={h}>ADMINISTRATIVE INTAKE — mandatory referral decision</div>
          <div className="flex gap-2 mb-2">
            <select data-testid="ia-referral-select" className={inp} value={referral} onChange={(e) => setReferral(e.target.value)}>
              {(dash?.referral_destinations || []).map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
          <input data-testid="ia-intake-note" className={`${inp} mb-2`} placeholder="Intake note…" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="flex gap-1.5 flex-wrap">
            <button data-testid="ia-intake-accept" onClick={() => act("intake", { decision: "accept", note, referral_to: referral }, "Complaint accepted for investigation")} className={btn} style={{ background: "#10B981" }}>ACCEPT</button>
            <button data-testid="ia-intake-info" onClick={() => act("intake", { decision: "request_info", note }, "Information requested from complainant")} className={btn} style={{ background: "#3B82F6" }}>REQUEST INFO</button>
            <button data-testid="ia-intake-refer" onClick={() => act("intake", { decision: "refer", note, referral_to: referral }, "Referred")} className={btn} style={{ background: "#F59E0B" }}>REFER</button>
            <button data-testid="ia-intake-duplicate" onClick={() => act("intake", { decision: "duplicate", note }, "Closed as duplicate")} className={btn} style={{ background: "#6B7280" }}>DUPLICATE</button>
            <button data-testid="ia-intake-close" onClick={() => act("intake", { decision: "admin_closure", note }, "Administrative closure")} className={btn} style={{ background: "#6B7280" }}>ADMIN CLOSURE</button>
          </div>
        </div>
      )}
      {sel.kind === "complaint" && !closed && stage === "assigned" && (
        <div className={sec}>
          <div className={h}>ASSIGN INVESTIGATOR</div>
          <div className="flex gap-2">
            <input data-testid="ia-investigator-input" className={inp} placeholder="Investigator name" value={investigator} onChange={(e) => setInvestigator(e.target.value)} />
            <button data-testid="ia-assign-btn" onClick={() => investigator.trim() && act("assign", { investigator }, "Investigator assigned")} className={btn} style={{ background: "#3B82F6" }}>ASSIGN</button>
          </div>
        </div>
      )}
      {sel.kind === "complaint" && sel.possible_criminal && !sel.criminal_review && !closed && (
        <div className={sec} style={{ background: "#2A1215" }}>
          <div className={h} style={{ color: "#F87171" }}>CRIMINAL REFERRAL REVIEW (required)</div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <select data-testid="ia-crim-decision" className={inp} value={crim.referral_decision} onChange={(e) => setCrim({ ...crim, referral_decision: e.target.value })}>
              <option value="referred">Refer for criminal investigation</option>
              <option value="declined_with_reason">Decline referral (documented)</option>
            </select>
            <input data-testid="ia-crim-agency" className={inp} placeholder="Agency referred to" value={crim.agency_referred_to} onChange={(e) => setCrim({ ...crim, agency_referred_to: e.target.value })} />
            <input className={inp} placeholder="Referral number (optional)" value={crim.referral_number} onChange={(e) => setCrim({ ...crim, referral_number: e.target.value })} />
            <input data-testid="ia-crim-reason" className={inp} placeholder="Reason (required)" value={crim.reason} onChange={(e) => setCrim({ ...crim, reason: e.target.value })} />
          </div>
          <button data-testid="ia-crim-submit" onClick={() => act("criminal-review", crim, "Criminal referral review documented")} className={btn} style={{ background: "#DC2626" }}>DOCUMENT REVIEW</button>
        </div>
      )}
      {sel.kind === "complaint" && !closed && stage === "investigation" && (
        <div className={sec}>
          <div className={h}>ALLEGATION FINDINGS (investigator: {sel.investigator || "—"})</div>
          {sel.categories.map((c) => (
            <div key={c} className="flex items-center gap-2 mb-1.5">
              <div className="text-xs text-[#C9CDD4] flex-1">{c}</div>
              <select data-testid={`ia-finding-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} className={`${inp} !w-56`} value={findings[c] || ""} onChange={(e) => setFindings({ ...findings, [c]: e.target.value })}>
                <option value="">Select finding…</option>
                {(dash?.finding_options || []).map((f) => <option key={f}>{f}</option>)}
              </select>
            </div>
          ))}
          <button data-testid="ia-findings-submit" onClick={() => {
            const list = sel.categories.filter((c) => findings[c]).map((c) => ({ allegation: c, finding: findings[c] }));
            if (list.length !== sel.categories.length) { toast.error("Select a finding for every allegation."); return; }
            act("findings", { findings: list }, "Findings submitted for supervisor review");
          }} className={`${btn} mt-1`} style={{ background: "#8B5CF6" }}><FileText size={12} className="inline mr-1" />SUBMIT FINDINGS</button>
        </div>
      )}
      {sel.kind === "complaint" && !closed && (stage === "supervisor_review" || stage === "command_review" || stage === "final") && (
        <div className={sec}>
          <div className={h}>APPROVAL CHAIN — {stage.replace(/_/g, " ").toUpperCase()}</div>
          {(sel.allegation_findings || []).map((f, i) => (
            <div key={i} className="text-xs text-[#C9CDD4] mb-0.5">{f.allegation}: <b className="text-white">{f.finding}</b></div>))}
          <input data-testid="ia-approve-reason" className={`${inp} my-2`} placeholder="Decision reason (required for return/modify)…" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="flex gap-1.5">
            <button data-testid="ia-approve-btn" onClick={() => act("approve", { level: stage === "supervisor_review" ? "supervisor" : stage === "command_review" ? "command" : "final", decision: "approve", reason: note }, "Approved — advanced to next level")} className={btn} style={{ background: "#10B981" }}>APPROVE ({stage === "supervisor_review" ? "IA SUPERVISOR" : stage === "command_review" ? "COMMANDER" : "FINAL — ADMINISTRATOR"})</button>
            <button data-testid="ia-return-btn" onClick={() => act("approve", { level: stage === "supervisor_review" ? "supervisor" : stage === "command_review" ? "command" : "final", decision: "return", reason: note }, "Returned for investigation")} className={btn} style={{ background: "#F59E0B" }}>RETURN</button>
          </div>
        </div>
      )}
      <div className={sec}>
        <div className={h}>RESPONSES TO COMPLAINANT (approval required before release)</div>
        <div className="space-y-1.5 mb-2">
          {(sel.messages || []).map((m, i) => (
            <div key={i} className="p-2 rounded-sm text-xs border" style={{ borderColor: m.public ? "#2C2D35" : "#F59E0B", background: "#141518" }}>
              <div className="font-mono text-[9px] text-[#9CA3AF] flex items-center gap-2">
                {m.author} · {new Date(m.ts).toLocaleString()}
                {!m.public && <span className="px-1.5 rounded-sm bg-[#F59E0B] text-black font-bold">DRAFT — NOT RELEASED</span>}
                {!m.public && <button data-testid={`ia-release-${i}`} onClick={() => act("respond/release", { index: i }, "Response approved and released to complainant")} className="ml-auto px-1.5 py-0.5 rounded-sm bg-[#10B981] text-white font-bold text-[8px]">APPROVE + RELEASE</button>}
              </div>
              <div className="text-[#E5E7EB] mt-0.5">{m.text}</div>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input data-testid="ia-draft-input" className={inp} placeholder="Draft a response to the complainant…" value={draft} onChange={(e) => setDraft(e.target.value)} />
          <button data-testid="ia-draft-send" onClick={() => draft.trim() && (act("respond", { text: draft }, "Draft saved — awaiting approval"), setDraft(""))} className={btn} style={{ background: "#3B82F6" }}><Send size={13} /></button>
        </div>
      </div>
      <div className={sec}>
        <div className={h}>IMMUTABLE AUDIT LOG</div>
        {(sel.audit || []).slice().reverse().map((a, i) => (
          <div key={i} className="font-mono text-[10px] text-[#9CA3AF] py-0.5 border-b border-[#1E1F24]">
            {new Date(a.ts).toLocaleString()} · <span className="text-[#E5E7EB]">{a.by}</span> ({a.role}) — {a.action.replace(/_/g, " ").toUpperCase()}{a.detail ? ` · ${a.detail}` : ""}
          </div>
        ))}
        {(sel.approvals || []).map((a, i) => (
          <div key={`ap${i}`} className="font-mono text-[10px] text-[#10B981] py-0.5">{a.electronic_signature} — {a.level} {a.decision} · {new Date(a.ts).toLocaleString()}</div>
        ))}
        {!(sel.audit || []).length && <div className="font-mono text-[10px] text-[#6B7280]">No actions yet</div>}
      </div>
    </div>
  );
}

function StaffUpload({ no, count, onDone }) {
  const [files, setFiles] = useState([]);
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState(0);
  const upload = async () => {
    if (!files.length) { toast.error("Choose files first"); return; }
    if (count + files.length > 100) { toast.error(`Limit is 100 files per request (${count} already attached)`); return; }
    setBusy(true); setProg(0);
    try {
      const fd = new FormData(); fd.append("description", desc); files.forEach((f) => fd.append("files", f));
      const { data } = await client.post(`/ia/records-requests/${no}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProg(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
      });
      toast.success(`${data.attachments.length} file(s) released to ${no} (${data.total}/${data.max})`);
      setFiles([]); setDesc(""); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  return (
    <div data-testid={`ia-records-upload-${no}`} className="mt-2 p-2 border border-dashed border-[#2C2D35] rounded-sm flex flex-wrap items-center gap-2">
      <input data-testid={`ia-records-upload-input-${no}`} type="file" multiple accept=".mp3,.wav,.m4a,.pdf,.doc,.docx,.txt"
        onChange={(e) => setFiles(Array.from(e.target.files || []))} className="text-[10px] font-mono text-[#9CA3AF] file:mr-2 file:px-2 file:py-1 file:text-[10px] file:font-head file:font-bold file:border-0 file:bg-[#1E2634] file:text-[#93C5FD]" />
      <input data-testid={`ia-records-upload-note-${no}`} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Note to requester (optional)"
        className="flex-1 min-w-[160px] bg-[#0d0e11] border border-[#2C2D35] px-2 py-1 text-[11px] text-[#F3F4F6] outline-none focus:border-[#22D3EE]" />
      <button data-testid={`ia-records-upload-btn-${no}`} onClick={upload} disabled={busy || !files.length}
        className="px-2.5 py-1 text-[10px] font-head font-bold rounded-sm text-black disabled:opacity-50 flex items-center gap-1" style={{ background: "#22C55E" }}>
        <Upload size={11} /> {busy ? `UPLOADING ${prog}%` : `RELEASE ${files.length || ""} FILE${files.length === 1 ? "" : "S"}`}
      </button>
      <span className="text-[9px] font-mono text-[#6B7280]">{count}/100 · WAV · MP3 · M4A · PDF · DOC · DOCX · TXT · 50MB each</span>
    </div>
  );
}

function RecordsView() {
  const [reqs, setReqs] = useState([]);
  const [resp, setResp] = useState({});
  const [confirmClose, setConfirmClose] = useState(null);
  const [staff, setStaff] = useState([]);
  const [me, setMe] = useState("");
  const [responder, setResponder] = useState({});
  const [queue, setQueue] = useState("all");
  const shownReqs = reqs.filter((r) => queue === "all" ? true : queue === "mine" ? (r.assigned_to && r.assigned_to === me) : queue === "unassigned" ? (!r.assigned_to && r.status !== "closed") : r.status !== "closed");
  const load = useCallback(async () => { setReqs((await client.get("/ia/records-requests")).data); }, []);
  useEffect(() => { load(); client.get("/ia/staff").then(({ data }) => { setStaff(data.staff || []); setMe(data.me || ""); }).catch(() => {}); }, [load]);
  const who = (r) => responder[r.request_no] || r.assigned_to || me;
  const assign = async (no, name) => {
    setResponder({ ...responder, [no]: name });
    try { await client.post(`/ia/records-requests/${no}/assign`, { responder: name }); toast.success(`Assigned to ${name}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Assign failed"); }
  };
  const act = async (no, payload, msg) => {
    const r = reqs.find((x) => x.request_no === no);
    try { await client.post(`/ia/records-requests/${no}/action`, { ...payload, responder: r ? who(r) : undefined }); toast.success(msg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };
  const packet = async (no, attach) => {
    try {
      const r = await client.get(`/ia/records-requests/${no}/fulfillment-packet.pdf`, { params: attach ? { attach: 1 } : {}, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `FulfillmentPacket_${no}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(attach ? `Packet generated and released to ${no}` : `Packet downloaded for ${no}`);
      if (attach) load();
    } catch (e) {
      const txt = e.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : e.response?.data?.detail;
      toast.error(txt || "Packet generation failed");
    }
  };
  return (
    <div data-testid="ia-records-view" className="border border-[#2C2D35] bg-[#16171B] divide-y divide-[#2C2D35]">
      <div className="flex items-center gap-3 px-4 py-2.5 bg-[#0d0e11]">
        <img src="/brand/records-seal.png" alt="Matcom Records Division" data-testid="ia-records-seal" className="w-10 h-10 rounded-full" />
        <div><div className="font-head font-bold text-sm text-[#F3F4F6]">MATCOM RECORDS DIVISION</div><div className="text-[10px] font-mono text-[#9CA3AF]">Public records / FOIA request queue · 14-day retention</div></div>
        <div className="ml-auto flex items-center gap-1" data-testid="ia-records-queue-filter">
          {[["all", "All"], ["open", "Open"], ["mine", `My Queue${me ? ` (${reqs.filter((r) => r.assigned_to === me).length})` : ""}`], ["unassigned", "Unassigned"]].map(([k, l]) => (
            <button key={k} data-testid={`ia-records-queue-${k}`} onClick={() => setQueue(k)} className="px-2 py-1 text-[10px] font-head rounded-sm uppercase"
              style={queue === k ? { background: "#B98A1E", color: "#000" } : { color: "#9CA3AF" }}>{l}</button>))}
        </div>
      </div>
      {!reqs.length && <div className="p-8 text-center font-mono text-xs text-[#6B7280]">No public records requests</div>}
      {reqs.length > 0 && !shownReqs.length && <div data-testid="ia-records-queue-empty" className="p-8 text-center font-mono text-xs text-[#6B7280]">{queue === "mine" ? "Nothing assigned to you — pick a request and assign yourself." : "No requests in this view"}</div>}
      {shownReqs.map((r) => (
        <div key={r.request_no} data-testid={`ia-records-${r.request_no}`} className="p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs font-bold text-white">{r.request_no}</span>
            <span className="text-[10px] font-mono text-[#9CA3AF]">{r.name} · {r.email || r.phone}</span>
            <span className="ml-auto px-1.5 py-0.5 text-[9px] font-bold rounded-sm" style={{ background: r.status === "closed" ? "#14432A" : "#1E2634", color: r.status === "closed" ? "#4ade80" : "#93C5FD" }}>
              {r.status === "closed" ? `CLOSED — ${r.disposition}${r.fulfilled_by ? ` · ${r.fulfilled_by}` : ""}` : r.current_stage.replace(/_/g, " ").toUpperCase()}{r.status !== "closed" && r.assigned_to ? ` · ${r.assigned_to}` : ""}
            </span>
          </div>
          <div className="text-[11px] text-[#C9CDD4] mt-1">{r.record_types.join(", ")} · {r.description}</div>
          {(r.attachments || []).length > 0 && (
            <div data-testid={`ia-records-attachments-${r.request_no}`} className="flex flex-wrap gap-1.5 mt-1.5">
              {r.attachments.map((a) => (
                <button key={a.aid} data-testid={`ia-records-att-${a.aid}`} onClick={async () => {
                  try {
                    const res = await client.get(`/ia/records-requests/${r.request_no}/attachments/${a.aid}`, { responseType: "blob" });
                    window.open(URL.createObjectURL(new Blob([res.data], { type: a.content_type })), "_blank");
                  } catch (e) { toast.error(e.response?.status === 410 ? "Expired — purged under 14-day retention" : "Unable to open file"); }
                }} className="px-2 py-1 text-[10px] font-mono rounded-sm border hover:bg-[#141518]" style={a.source === "agency" ? { borderColor: "#22C55E", color: "#22C55E" } : { borderColor: "#2C2D35", color: "#22D3EE" }}>
                  {a.source === "agency" ? "⬆ RELEASED" : a.content_type?.startsWith("audio/") ? "🔊" : "📄"} {a.aid} · {a.filename}
                </button>
              ))}
            </div>
          )}
          {r.expires_at && <div className="text-[10px] font-mono mt-1" style={{ color: r.purged ? "#EF4444" : "#F59E0B" }}>{r.purged ? "PURGED — 14-day retention" : `Expires ${new Date(r.expires_at).toLocaleDateString()} (14-day retention)`}{!r.purged && r.expiry_reminder_sent && <span data-testid={`ia-records-reminder-${r.request_no}`} className="ml-2 text-[#22D3EE]">· 2-DAY REMINDER SENT</span>}</div>}
          {r.status === "closed" && !r.purged && (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`ia-rec-closed-${r.request_no}`}>
              <span className="text-[10px] font-mono text-[#9CA3AF]">Closed — {r.disposition || "no disposition"}. Uploads, releases and packets are disabled until reopened.</span>
              <button data-testid={`ia-rec-reopen-${r.request_no}`}
                onClick={() => { const why = window.prompt(`Reopen ${r.request_no}? Enter a reason (shown to requester):`, "Request reopened for additional processing."); if (why != null) act(r.request_no, { stage: "reopen", response: why }, "Request reopened"); }}
                className="px-2.5 py-1 text-[10px] font-head font-bold rounded-sm text-black flex items-center gap-1" style={{ background: "#F59E0B" }}>
                <RefreshCw size={11} /> REOPEN REQUEST
              </button>
            </div>
          )}
          {r.status !== "closed" && !r.purged && <StaffUpload no={r.request_no} count={(r.attachments || []).length} onDone={load} />}
          {!r.purged && r.status !== "closed" && (r.attachments || []).some((a) => a.source === "agency") && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button data-testid={`ia-records-packet-${r.request_no}`} onClick={() => packet(r.request_no, false)}
                className="px-2.5 py-1 text-[10px] font-head font-bold rounded-sm text-black flex items-center gap-1" style={{ background: "#F59E0B" }}>
                <FileText size={11} /> FULFILLMENT PACKET PDF
              </button>
              <button data-testid={`ia-records-packet-release-${r.request_no}`} onClick={() => packet(r.request_no, true)}
                className="px-2.5 py-1 text-[10px] font-head font-bold rounded-sm border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10 flex items-center gap-1">
                <Send size={11} /> GENERATE &amp; RELEASE TO REQUESTER
              </button>
              <span className="text-[9px] font-mono text-[#6B7280]">transmittal letter + index + all released PDFs, sealed</span>
            </div>
          )}
          {r.status !== "closed" && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              <label className="text-[9px] font-mono text-[#6B7280] flex items-center gap-1">RESPONDING:
                <select data-testid={`ia-rec-responder-${r.request_no}`} value={who(r)} onChange={(e) => assign(r.request_no, e.target.value)}
                  className="bg-[#0d0e11] border border-[#F59E0B] text-[#F59E0B] px-1.5 py-1 text-[10px] font-head font-bold outline-none">
                  {!staff.some((s) => s.name === who(r)) && who(r) && <option value={who(r)}>{who(r)}</option>}
                  {staff.map((s) => <option key={s.username || s.name} value={s.name}>{s.name}{s.badge ? ` · ${s.badge}` : ""}</option>)}
                </select>
              </label>
              <span className="w-px h-4 bg-[#2C2D35]" />
              <button data-testid={`ia-rec-review-${r.request_no}`} onClick={() => act(r.request_no, { stage: "under_review" }, "Marked under review")} className="px-2 py-1 text-[9px] font-head font-bold rounded-sm text-white bg-[#3B82F6]">UNDER REVIEW</button>
              <button data-testid={`ia-rec-process-${r.request_no}`} onClick={() => act(r.request_no, { stage: "processing" }, "Marked processing")} className="px-2 py-1 text-[9px] font-head font-bold rounded-sm text-white bg-[#8B5CF6]">PROCESSING</button>
              {["Fulfilled", "Partially Fulfilled", "Denied"].map((d) => (
                <button key={d} data-testid={`ia-rec-final-${d.replace(/\s/g, "-").toLowerCase()}-${r.request_no}`} onClick={() => setConfirmClose({ r, d })}
                  className="px-2 py-1 text-[9px] font-head font-bold rounded-sm text-white" style={{ background: d === "Denied" ? "#DC2626" : "#10B981" }}>{d.toUpperCase()}</button>
              ))}
              <input data-testid={`ia-rec-resp-${r.request_no}`} value={resp[r.request_no] || ""} onChange={(e) => setResp({ ...resp, [r.request_no]: e.target.value })}
                placeholder="Message to requester…" className="flex-1 min-w-[180px] bg-[#141518] border border-[#2C2D35] px-2 py-1 text-[10px] outline-none font-mono text-white" />
              <button data-testid={`ia-rec-send-${r.request_no}`} onClick={() => resp[r.request_no]?.trim() && act(r.request_no, { response: resp[r.request_no] }, "Response sent")} className="px-2 py-1 text-[9px] font-head font-bold rounded-sm text-white bg-[#6B7280]">SEND</button>
            </div>
          )}
        </div>
      ))}
      <AlertDialog open={!!confirmClose} onOpenChange={(o) => !o && setConfirmClose(null)}>
        <AlertDialogContent data-testid="ia-rec-close-confirm" className="bg-[#16171B] border-[#2C2D35] text-[#F3F4F6]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-head flex items-center gap-2">
              <TriangleAlert size={18} style={{ color: confirmClose?.d === "Denied" ? "#DC2626" : "#F59E0B" }} />
              Close request {confirmClose?.r.request_no} as {confirmClose?.d?.toUpperCase()}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-[#9CA3AF] space-y-2">
                <div>Requester: <b className="text-[#F3F4F6]">{confirmClose?.r.name}</b> · {confirmClose?.r.record_types?.join(", ")}</div>
                <div>Fulfilled by: <b className="text-[#F59E0B]">{confirmClose ? who(confirmClose.r) : ""}</b></div>
                <div>Released files on this request: <b className="text-[#F3F4F6]">{(confirmClose?.r.attachments || []).filter((a) => a.source === "agency").length}</b>
                  {confirmClose?.d === "Fulfilled" && !(confirmClose?.r.attachments || []).some((a) => a.source === "agency") && <span className="ml-2 text-[#F59E0B]">— nothing has been released yet</span>}</div>
                <div className="p-2 border border-[#2C2D35] bg-[#0d0e11] font-mono text-xs text-[#C9CDD4]">
                  Message to requester: "{confirmClose ? (resp[confirmClose.r.request_no] || `Your request has been completed: ${confirmClose.d}.`) : ""}"
                </div>
                <div>This closes the request and stops uploads and releases. You can reopen it later if needed, but the requester will be notified now.</div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ia-rec-close-cancel" className="bg-transparent border-[#2C2D35] text-[#F3F4F6] hover:bg-[#141518]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="ia-rec-close-confirm-btn" className="text-white font-head" style={{ background: confirmClose?.d === "Denied" ? "#DC2626" : "#10B981" }}
              onClick={() => { const { r, d } = confirmClose; setConfirmClose(null); act(r.request_no, { stage: "final", disposition: d, response: resp[r.request_no] || `Your request has been completed: ${d}.` }, `Completed — ${d}`); }}>
              Yes, close as {confirmClose?.d}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PhoneIntakeView({ onDone }) {
  const [cats, setCats] = useState({ complaint: [], commendation: [] });
  const [kind, setKind] = useState("complaint");
  const [sel, setSel] = useState([]);
  const [anon, setAnon] = useState(false);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [asked, setAsked] = useState({});
  useEffect(() => { client.get("/public/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const inp2 = "w-full bg-[#141518] border border-[#2C2D35] px-2 py-2 text-xs outline-none focus:border-[#10B981] font-mono text-white";
  // Interview sections in the order they are conducted — used for both rendering and the audit transcript.
  const sections = kind === "complaint" ? [
    { tid: "identification", title: "1. Initial Intake and Identification", qs: IDENTIFICATION_QUESTIONS, color: "#10B981" },
    { tid: "universal", title: "2. Ask in Every Complaint", qs: UNIVERSAL_QUESTIONS, color: "#3B82F6" },
    ...sel.filter((c) => CATEGORY_QUESTIONS[c]).map((c) => ({
      tid: c.replace(/[^a-z0-9]+/gi, "-").toLowerCase(), title: `Allegation — ${c}`, qs: CATEGORY_QUESTIONS[c], color: "#F59E0B", note: CATEGORY_NOTES[c],
    })),
    { tid: "closing", title: "Closing and Verification", qs: CLOSING_QUESTIONS, color: "#A78BFA" },
  ] : [];
  const toggleAsked = (tid) => (i) => setAsked((p) => ({ ...p, [`${tid}:${i}`]: !p[`${tid}:${i}`] }));
  const submit = async () => {
    setBusy(true);
    try {
      const interview_transcript = sections.map((s) => ({
        section: s.title, questions: s.qs.map((q, i) => ({ q, asked: !!asked[`${s.tid}:${i}`] })),
      }));
      const r = await client.post("/ia/phone-intake", { kind, categories: sel, anonymous: anon, ...f, ...(sections.length ? { interview_transcript } : {}) });
      setResult(r.data); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Intake failed"); }
    finally { setBusy(false); }
  };
  if (result) return (
    <div data-testid="phone-intake-result" className="border-2 border-[#10B981] bg-[#16171B] p-6 max-w-xl mx-auto text-center space-y-3">
      <CheckCircle2 size={36} className="mx-auto" style={{ color: "#10B981" }} />
      <div className="font-head font-bold text-white">CASE FILED — READ TO CALLER:</div>
      <div className="font-mono text-3xl font-bold text-white" data-testid="phone-intake-case">{result.case_number}</div>
      <div className="font-mono text-xl font-bold" style={{ color: "#F87171" }} data-testid="phone-intake-code">ACCESS CODE: {result.access_code}</div>
      <div className="text-xs text-[#9CA3AF]">Tell the caller to save both — they can track status and upload evidence at matcom-iedcad.org/public/case. Internal ref: {result.internal_reference}</div>
      <button data-testid="phone-intake-new" onClick={() => { setResult(null); setSel([]); setF({}); setAsked({}); }} className="px-4 py-2 text-xs font-head font-bold rounded-sm text-white bg-[#3B82F6]">NEW PHONE INTAKE</button>
    </div>
  );
  return (
    <div data-testid="phone-intake-form" className="border border-[#2C2D35] bg-[#16171B] p-4 max-w-3xl mx-auto space-y-3">
      <div className="font-head text-[10px] font-bold tracking-wider text-[#10B981]">TELEPHONE INTAKE — file on behalf of a caller (same validation and case numbering as the public portal)</div>
      <div className="flex gap-1.5">
        <button data-testid="phone-kind-complaint" onClick={() => { setKind("complaint"); setSel([]); }} className="px-3 py-1.5 text-[10px] font-head font-bold rounded-sm" style={kind === "complaint" ? { background: "#DC2626", color: "#fff" } : { background: "#141518", color: "#9CA3AF" }}>COMPLAINT</button>
        <button data-testid="phone-kind-commendation" onClick={() => { setKind("commendation"); setSel([]); }} className="px-3 py-1.5 text-[10px] font-head font-bold rounded-sm" style={kind === "commendation" ? { background: "#10B981", color: "#fff" } : { background: "#141518", color: "#9CA3AF" }}>COMMENDATION</button>
        <label className="ml-auto flex items-center gap-1.5 text-xs text-[#C9CDD4]"><input data-testid="phone-anon" type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} /> Caller wishes to remain anonymous</label>
      </div>
      <div className="flex flex-wrap gap-1">
        {(cats[kind] || []).map((c) => (
          <button key={c} data-testid={`phone-cat-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} onClick={() => setSel((p) => p.includes(c) ? p.filter((x) => x !== c) : [...p, c])}
            className="px-2 py-1 text-[10px] rounded-sm border" style={sel.includes(c) ? { background: "#1E2634", borderColor: "#3B82F6", color: "#93C5FD" } : { background: "#141518", borderColor: "#2C2D35", color: "#9CA3AF" }}>{c}</button>))}
      </div>
      {kind === "complaint" && (
        <div data-testid="phone-guided-questions" className="border border-[#2C2D35] bg-[#141518] p-3 space-y-3 max-h-96 overflow-y-auto">
          {sections.map((s, si) => (
            <Fragment key={s.tid}>
              <QSet title={s.title.toUpperCase()} qs={s.qs} color={s.color} tid={s.tid} note={s.note}
                checked={Object.fromEntries(s.qs.map((_, i) => [i, !!asked[`${s.tid}:${i}`]]))} onToggle={toggleAsked(s.tid)} />
              {si === 1 && !sel.length && <div className="font-mono text-[10px] text-[#6B7280]">Select allegation categories above to load allegation-specific interview questions.</div>}
            </Fragment>
          ))}
        </div>
      )}
      {!anon && (
        <div className="grid sm:grid-cols-3 gap-2">
          <input data-testid="phone-name" className={inp2} placeholder="Caller name *" value={f.name || ""} onChange={set("name")} />
          <input data-testid="phone-phone" className={inp2} placeholder="Caller phone *" value={f.phone || ""} onChange={set("phone")} />
          <input className={inp2} placeholder="Caller email" value={f.email || ""} onChange={set("email")} />
        </div>
      )}
      <div className="grid sm:grid-cols-4 gap-2">
        <input data-testid="phone-officer" className={inp2} placeholder={kind === "commendation" ? "Officer/employee name *" : "Officer/employee (if known)"} value={f.officer_name || ""} onChange={set("officer_name")} />
        <input type="date" className={inp2} value={f.incident_date || ""} onChange={set("incident_date")} />
        <input className={inp2} placeholder="Location" value={f.incident_location || ""} onChange={set("incident_location")} />
        <input className={inp2} placeholder="CAD # (if known)" value={f.cad_number || ""} onChange={set("cad_number")} />
      </div>
      <textarea data-testid="phone-description" className={inp2} rows={4} placeholder="Caller's statement (as told over the phone) *" value={f.description || ""} onChange={set("description")} />
      <button data-testid="phone-submit" disabled={busy} onClick={submit} className="w-full py-2.5 text-xs font-head font-bold rounded-sm text-white disabled:opacity-40" style={{ background: "#10B981" }}>
        {busy ? "FILING…" : "FILE CASE + GENERATE CASE NUMBER"}
      </button>
    </div>
  );
}

function QSet({ title, qs, color, tid, note, checked, onToggle }) {
  const done = Object.values(checked).filter(Boolean).length;
  return (
    <div data-testid={`qset-${tid}`}>
      <div className="font-head text-[9px] font-bold tracking-wider mb-1 flex items-center gap-2" style={{ color }}>
        {title} <span className="text-[#6B7280]">{done}/{qs.length} asked</span>
      </div>
      {note && <div data-testid={`qset-note-${tid}`} className="font-mono text-[10px] italic mb-1.5 px-2 py-1 border-l-2" style={{ color: "#FCA5A5", borderColor: "#DC2626", background: "#1C1517" }}>{note}</div>}
      {qs.map((q, i) => (
        <label key={i} className="flex items-start gap-2 py-0.5 cursor-pointer text-[11px]" style={{ color: checked[i] ? "#6B7280" : "#C9CDD4", textDecoration: checked[i] ? "line-through" : "none" }}>
          <input type="checkbox" className="mt-0.5" checked={!!checked[i]} onChange={() => onToggle(i)} />
          {q}
        </label>
      ))}
    </div>
  );
}
